import { Box, HStack, Text } from '@chakra-ui/react'
import { NumberInput } from '@chakra-ui/react'
import { EdgeLabelRenderer } from '@xyflow/react'
import debounce from 'lodash/debounce'
import type { FC, FocusEventHandler } from 'react'
import { useCallback } from 'react'
import { forwardRef } from 'react'

import { InputGroup } from '@app/components/ui/input-group'
import { useStore } from '@app/store'
import type { DomainEdge } from '@app/types'

type Props = {
  edge: DomainEdge
  x: number
  y: number
}

type InputProps = Omit<NumberInput.RootProps, 'children'> & {
  edge: DomainEdge
}

// Prevent interactions from e.g. zooming the map
const stopPropagation = (event: React.MouseEvent) => event.stopPropagation()
const stopProps = { onClick: stopPropagation, onDoubleClick: stopPropagation }

const Input: FC<InputProps> = forwardRef<HTMLDivElement, InputProps>(({ edge, ...props }, ref) => {
  const updateEdge = useStore.use.updateObject()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce((updatedEdge: Partial<DomainEdge>) => updateEdge({ edge: updatedEdge }), 150),
    [updateEdge]
  )

  const onValueChange: NumberInput.RootProps['onValueChange'] = useCallback(
    ({ valueAsNumber }) => {
      const num = isNaN(valueAsNumber) ? 0 : valueAsNumber

      debouncedUpdate({ id: edge.edgeId, simImpact: num / 100 })
    },
    [edge.edgeId, debouncedUpdate]
  )

  const resetInput: FocusEventHandler<HTMLInputElement> = useCallback((e) => {
    if (isNaN(parseFloat(e.target.value))) {
      e.target.value = '0'
    }
  }, [])

  return (
    <NumberInput.Root ref={ref} onValueChange={onValueChange} fontSize="xl" {...props}>
      <NumberInput.Control
        w="auto"
        position="relative"
        bg="bg"
        p={2}
        borderRadius="md"
        border="border.emphasized"
        borderWidth="1px"
      >
        <HStack gap={1.5}>
          <NumberInput.DecrementTrigger fontSize="1.75rem" bgColor="red.subtle" {...stopProps} />
          <InputGroup endElement={<Text>%</Text>}>
            <NumberInput.Input onBlur={resetInput} px={1} maxW="10ch" {...stopProps} />
          </InputGroup>
          <NumberInput.IncrementTrigger fontSize="1.75rem" bgColor="green.subtle" {...stopProps} />
        </HStack>
      </NumberInput.Control>
    </NumberInput.Root>
  )
})

const SimImpactInput: FC<Props> = ({ edge, x, y }) => {
  const impact = edge?.simImpact || 0
  return (
    <EdgeLabelRenderer>
      <Box position="absolute" zIndex={2} transform={`translate(-50%, -50%) translate(${x}px,${y}px)`}>
        <Input edge={edge} pointerEvents="all" defaultValue={(impact * 100.0).toFixed(1).toString()} step={0.1} />
      </Box>
    </EdgeLabelRenderer>
  )
}

export default SimImpactInput
